import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import WayleaveDocuments from './WayleaveDocuments';
import WayleaveDocsSetup from './WayleaveDocsSetup';
import WayleaveDre from './WayleaveDre';

// Component to handle map view updates
function MapUpdater({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (center && zoom) {
      map.setView(center, zoom, { animate: true });
    }
  }, [center, zoom, map]);

  return null;
}

// Tab visibility is driven by the Settings → Roles access matrix.
const WAYLEAVE_TAB_ORDER = ['overview', 'documents', 'setup', 'dre'];

function Wayleave({ userInfo }) {
  // Tabs: Overview (map + table + details), Documents, Setup, and DRE — each
  // gated by the role's `wayleave.*` access items delivered in userInfo.screens.
  const screens = userInfo?.screens || [];
  const canOverview = screens.includes('wayleave.overview');
  const canDocuments = screens.includes('wayleave.documents');
  const canSetup = screens.includes('wayleave.setup');
  const canDre = screens.includes('wayleave.dre');
  const tabAccess = { overview: canOverview, documents: canDocuments, setup: canSetup, dre: canDre };
  const firstTab = WAYLEAVE_TAB_ORDER.find((t) => tabAccess[t]) || 'overview';

  // Deep-link support: /wayleave?tab=dre or ?pack=..&slot=.. (from notifications)
  const routerLocation = useLocation();
  const deepLink = React.useMemo(() => {
    const p = new URLSearchParams(routerLocation.search);
    return { tab: p.get('tab'), pack: p.get('pack'), slot: p.get('slot') };
  }, [routerLocation.search]);
  const initialTab = (deepLink.tab && tabAccess[deepLink.tab]) ? deepLink.tab
    : ((deepLink.pack || deepLink.slot) && canDocuments) ? 'documents'
    : firstTab;

  const [activeTab, setActiveTab] = useState(initialTab);

  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStation, setSelectedStation] = useState(null);
  const [search, setSearch] = useState('');
  const [wayleaveData, setWayleaveData] = useState([]);
  const [wayleaveLoading, setWayleaveLoading] = useState(false);
  const [wayleaveError, setWayleaveError] = useState('');
  const [stationInfo, setStationInfo] = useState(null);
  const [allWayleaveStations, setAllWayleaveStations] = useState([]);
  const [filteredWayleaveStations, setFilteredWayleaveStations] = useState([]);
  const [wayleaveTableLoading, setWayleaveTableLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(7);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const mapRef = useRef(null);

  // Table sorting and filtering state
  const [sortColumn, setSortColumn] = useState('StationNo');
  const [sortDirection, setSortDirection] = useState('asc');
  const [stationFilter, setStationFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // "Has notes" filter — only show stations that have at least one note
  const [notesOnly, setNotesOnly] = useState(false);
  const [stationsWithNotes, setStationsWithNotes] = useState(new Set());

  // Ref for table container to enable scrolling to top
  const tableContainerRef = useRef(null);

  // Notes state
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  // @mention state
  const [mentionUsers, setMentionUsers] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionCursorPos, setMentionCursorPos] = useState(null);
  const noteInputRef = useRef(null);

  useEffect(() => {
    fetchLocations();
    fetchAllWayleaveStations();
    fetchStationsWithNotes();
  }, []);

  // If the active tab isn't accessible for this role, fall back to the first one
  useEffect(() => {
    if (!tabAccess[activeTab]) setActiveTab(firstTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canOverview, canDocuments, canSetup, canDre]);

  // Follow a deep-link (e.g. clicking a notification) to the right tab
  useEffect(() => {
    if (deepLink.tab && tabAccess[deepLink.tab]) setActiveTab(deepLink.tab);
    else if ((deepLink.pack || deepLink.slot) && canDocuments) setActiveTab('documents');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  // Resize the Leaflet map when toggling between inline and expanded views
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [isMapExpanded]);

  // Allow Escape to close the expanded map
  useEffect(() => {
    if (!isMapExpanded) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setIsMapExpanded(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isMapExpanded]);

  useEffect(() => {
    let filteredLocs = locations;
    if (search.trim() !== '') {
      const searchLower = search.toLowerCase();
      filteredLocs = filteredLocs.filter(location => {
        return (
          (location.STATION_NO && location.STATION_NO.toString().toLowerCase().includes(searchLower)) ||
          (location.Owner && location.Owner.toLowerCase().includes(searchLower)) ||
          (location.Description && location.Description.toLowerCase().includes(searchLower)) ||
          (location.StationType && location.StationType.toLowerCase().includes(searchLower)) ||
          (location.Route && location.Route.toLowerCase().includes(searchLower)) ||
          (location.Road && location.Road.toLowerCase().includes(searchLower)) ||
          (location.Stratum && location.Stratum.toString().toLowerCase().includes(searchLower))
        );
      });
    }
    if (notesOnly) {
      filteredLocs = filteredLocs.filter(location =>
        location.STATION_NO != null && stationsWithNotes.has(String(location.STATION_NO))
      );
    }
    setFilteredLocations(filteredLocs);
  }, [search, locations, notesOnly, stationsWithNotes]);

  // Separate effect for table filtering and sorting
  useEffect(() => {
    let filtered = [...allWayleaveStations];

    // Apply global search filter
    if (search.trim() !== '') {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(station => {
        return (
          (station.StationNo && station.StationNo.toString().toLowerCase().includes(searchLower)) ||
          (station.Description && station.Description.toLowerCase().includes(searchLower)) ||
          (station.Owner && station.Owner.toLowerCase().includes(searchLower)) ||
          (station.ProgressStatus && station.ProgressStatus.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply column filters
    if (stationFilter.trim() !== '') {
      filtered = filtered.filter(station =>
        station.StationNo && station.StationNo.toString().toLowerCase().includes(stationFilter.toLowerCase())
      );
    }
    if (descriptionFilter.trim() !== '') {
      filtered = filtered.filter(station =>
        station.Description && station.Description.toLowerCase().includes(descriptionFilter.toLowerCase())
      );
    }
    if (ownerFilter.trim() !== '') {
      filtered = filtered.filter(station =>
        station.Owner && station.Owner.toLowerCase().includes(ownerFilter.toLowerCase())
      );
    }
    if (statusFilter.trim() !== '') {
      filtered = filtered.filter(station =>
        station.ProgressStatus && station.ProgressStatus.toLowerCase().includes(statusFilter.toLowerCase())
      );
    }

    // Apply "has notes" filter
    if (notesOnly) {
      filtered = filtered.filter(station =>
        station.StationNo != null && stationsWithNotes.has(String(station.StationNo))
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Convert to string for comparison
      aVal = aVal.toString().toLowerCase();
      bVal = bVal.toString().toLowerCase();

      if (sortDirection === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });

    setFilteredWayleaveStations(filtered);
  }, [search, allWayleaveStations, stationFilter, descriptionFilter, ownerFilter, statusFilter, notesOnly, stationsWithNotes, sortColumn, sortDirection]);

  const fetchStationsWithNotes = async () => {
    try {
      const response = await axios.get('/api/wayleave-notes/stations/with-notes', { withCredentials: true });
      setStationsWithNotes(new Set((response.data || []).map(String)));
    } catch (err) {
      console.error('Error fetching stations with notes:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/locations/wayleave/map', { withCredentials: true });
      setLocations(response.data);
      setFilteredLocations(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load location data');
      console.error('Locations error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllWayleaveStations = async () => {
    try {
      setWayleaveTableLoading(true);
      const response = await axios.get('/api/locations/wayleave/all', { withCredentials: true });
      setAllWayleaveStations(response.data);
      setFilteredWayleaveStations(response.data);
    } catch (err) {
      console.error('Error fetching all wayleave stations:', err);
    } finally {
      setWayleaveTableLoading(false);
    }
  };

  const fetchWayleaveData = async (stationNo) => {
    try {
      setWayleaveLoading(true);
      setWayleaveError('');
      const response = await axios.get(`/api/locations/${stationNo}/wayleave`, { withCredentials: true });
      setWayleaveData(response.data);
    } catch (err) {
      setWayleaveError('Failed to load wayleave data');
      console.error('Wayleave error:', err);
      setWayleaveData([]);
    } finally {
      setWayleaveLoading(false);
    }
  };

  // ---- Notes ----
  const fetchNotes = async (stationNo) => {
    try {
      setNotesLoading(true);
      const response = await axios.get(`/api/wayleave-notes/${stationNo}`, { withCredentials: true });
      setNotes(response.data);
    } catch (err) {
      console.error('Error fetching notes:', err);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  };

  const saveNote = async () => {
    if (!selectedStation || !noteText.trim()) return;
    try {
      setNoteSaving(true);
      const response = await axios.post('/api/wayleave-notes', {
        stationNo: selectedStation,
        noteText: noteText.trim(),
      }, { withCredentials: true });
      setNotes(response.data);
      setNoteText('');
    } catch (err) {
      console.error('Error saving note:', err);
    } finally {
      setNoteSaving(false);
    }
  };

  const updateNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    try {
      const response = await axios.put(`/api/wayleave-notes/${noteId}`, {
        noteText: editingNoteText.trim(),
      }, { withCredentials: true });
      setNotes(response.data);
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (err) {
      console.error('Error updating note:', err);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const response = await axios.delete(`/api/wayleave-notes/${noteId}`, { withCredentials: true });
      setNotes(response.data);
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  // Fetch mentionable users on mount
  useEffect(() => {
    axios.get('/api/users/mentionable', { withCredentials: true })
      .then(res => setMentionUsers(res.data))
      .catch(() => {});
  }, []);

  // Fetch notes whenever the selected station changes
  useEffect(() => {
    if (selectedStation) {
      fetchNotes(selectedStation);
    } else {
      setNotes([]);
    }
    setNoteText('');
    setEditingNoteId(null);
    setEditingNoteText('');
    setShowMentionDropdown(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStation]);

  // @mention handlers
  const handleNoteChange = (e) => {
    const val = e.target.value;
    setNoteText(val);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\S*)$/);

    if (mentionMatch) {
      setMentionFilter(mentionMatch[1].toLowerCase());
      setMentionCursorPos(mentionMatch.index);
      setShowMentionDropdown(true);
      setMentionIndex(0);
    } else {
      setShowMentionDropdown(false);
    }
  };

  const getFilteredMentionUsers = () => {
    return mentionUsers.filter(u => {
      const search = mentionFilter.toLowerCase();
      return u.username.toLowerCase().includes(search) ||
        (u.firstName && u.firstName.toLowerCase().includes(search)) ||
        (u.lastName && u.lastName.toLowerCase().includes(search));
    });
  };

  const insertMention = (user) => {
    const before = noteText.slice(0, mentionCursorPos);
    const afterCursor = noteInputRef.current?.selectionStart || (mentionCursorPos + mentionFilter.length + 1);
    const after = noteText.slice(afterCursor);
    const newText = before + '@' + user.username + ' ' + after;
    setNoteText(newText);
    setShowMentionDropdown(false);

    // Refocus textarea at correct position
    setTimeout(() => {
      const pos = before.length + user.username.length + 2; // @username + space
      if (noteInputRef.current) {
        noteInputRef.current.focus();
        noteInputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleNoteKeyDown = (e) => {
    if (!showMentionDropdown) return;
    const filtered = getFilteredMentionUsers();
    if (filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(filtered[mentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
    }
  };

  // Render note text with @mention highlighting
  const renderNoteText = (text) => {
    const parts = text.split(/(@\S+)/g);
    return parts.map((part, i) =>
      /^@\S+/.test(part) ? (
        <span key={i} className="inline-block bg-amber-100 text-amber-800 px-1 rounded text-xs font-medium">{part}</span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  const handleStationClick = (stationNo) => {
    setSelectedStation(stationNo);
    fetchWayleaveData(stationNo);

    // First check locations array (has more detail), fallback to allWayleaveStations
    let station = locations.find(loc => loc.STATION_NO === stationNo);

    // If not in map locations, check the table data
    if (!station) {
      station = allWayleaveStations.find(s => s.StationNo === stationNo);
      // Convert field names to match expected format
      if (station) {
        station = {
          STATION_NO: station.StationNo,
          LAT: station.LAT,
          LONG: station.LONG,
          Owner: station.Owner,
          Description: station.Description
        };
      }
    }

    if (station) {
      setStationInfo(station);
      if (station.LAT && station.LONG) {
        const lat = parseFloat(station.LAT);
        const lng = parseFloat(station.LONG);
        if (!isNaN(lat) && !isNaN(lng)) {
          setMapCenter([lat, lng]);
          setMapZoom(14);
        }
      }
    }

    // Scroll table to the selected station row
    setTimeout(() => {
      const rowIndex = filteredWayleaveStations.findIndex(s => s.StationNo === stationNo);
      if (rowIndex !== -1 && tableContainerRef.current) {
        // Each row is approximately 48px high, plus header (~80px)
        const headerHeight = 80;
        const rowHeight = 48;
        const scrollPosition = headerHeight + (rowIndex * rowHeight);
        tableContainerRef.current.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleReset = () => {
    // Clear selection
    setSelectedStation(null);
    setStationInfo(null);
    setWayleaveData([]);

    // Clear search first
    setSearch('');

    // Clear table filters
    setStationFilter('');
    setDescriptionFilter('');
    setOwnerFilter('');
    setStatusFilter('');
    setNotesOnly(false);

    // Reset sorting
    setSortColumn('StationNo');
    setSortDirection('asc');

    // Scroll table to top
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }

    // Reset map - calculate default center to trigger MapUpdater
    setTimeout(() => {
      const defaultCenter = getMapCenter();
      setMapCenter(defaultCenter);
      setMapZoom(7);
    }, 100);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setStationFilter('');
    setDescriptionFilter('');
    setOwnerFilter('');
    setStatusFilter('');
  };

  // Calculate map center (average of all coordinates)
  const getMapCenter = () => {
    const dataToUse = filteredLocations.length > 0 ? filteredLocations : locations;
    if (dataToUse.length === 0) return [-28.4793, 24.6727]; // Default: South Africa center

    const avgLat = dataToUse.reduce((sum, loc) => sum + parseFloat(loc.LAT), 0) / dataToUse.length;
    const avgLong = dataToUse.reduce((sum, loc) => sum + parseFloat(loc.LONG), 0) / dataToUse.length;

    return [avgLat, avgLong];
  };

  // Get color based on progress status
  const getStatusColor = (status) => {
    const statusColors = {
      'H O Docs Received': '#fbbf24',        // yellow
      'HO Docs Checked': '#f97316',          // orange
      'HO Docs Submitted': '#ea580c',        // dark orange
      'WCG Approval Obtained': '#3b82f6',    // blue
      'As-built Submitted': '#10b981',       // green
      'No Activity': '#9ca3af'               // gray
    };
    return statusColors[status] || '#9ca3af';
  };

  // Helper function to convert hex color to rgba with transparency
  const hexToRgba = (hex, alpha = 0.7) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Create custom marker icon for wayleave stations
  const createWayleaveIcon = (progressStatus) => {
    const size = 20;
    const markerColorSolid = getStatusColor(progressStatus);
    const markerColorTransparent = hexToRgba(markerColorSolid, 0.7);

    return L.divIcon({
      html: `
        <div style="
          width: ${size}px;
          height: ${size}px;
          background-color: ${markerColorTransparent};
          border: 2px solid ${markerColorSolid};
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          cursor: pointer;
        "></div>
      `,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -(size / 2)]
    });
  };

  // Create pulsating overlay icon for selected station
  const createPulseIcon = (color) => {
    const size = 40;
    return L.divIcon({
      html: `
        <style>
          @keyframes wayleave-pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.7; }
            50% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
          }
        </style>
        <div style="position: relative; width: ${size}px; height: ${size}px;">
          <div style="
            position: absolute; top: 50%; left: 50%;
            transform: translate(-50%, -50%) scale(1);
            width: ${size}px; height: ${size}px;
            border-radius: 50%;
            background-color: ${color};
            opacity: 0.4;
            animation: wayleave-pulse 1.5s ease-in-out infinite;
          "></div>
          <div style="
            position: absolute; top: 50%; left: 50%;
            transform: translate(-50%, -50%);
            width: 14px; height: 14px;
            border-radius: 50%;
            background-color: ${color};
            border: 2px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          "></div>
        </div>
      `,
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  };

  return (
    <div className="w-full overflow-hidden">
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-1">
          {canOverview && (
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          )}
          {canDocuments && (
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                activeTab === 'documents'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Documents
            </button>
          )}
          {canSetup && (
            <button
              onClick={() => setActiveTab('setup')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                activeTab === 'setup'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Setup
            </button>
          )}
          {canDre && (
            <button
              onClick={() => setActiveTab('dre')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                activeTab === 'dre'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              DRE
            </button>
          )}
        </nav>
      </div>

      {activeTab === 'documents' && canDocuments ? (
        <WayleaveDocuments userInfo={userInfo} deepLink={(deepLink.pack || deepLink.slot) ? deepLink : null} />
      ) : activeTab === 'setup' && canSetup ? (
        <WayleaveDocsSetup userInfo={userInfo} />
      ) : activeTab === 'dre' && canDre ? (
        <WayleaveDre />
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading map...</div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      ) : (
      <>
      {/* Search and Filters */}
      <div className="card p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          {/* Search */}
          <div className="md:col-span-6 lg:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Search</label>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search stations..."
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <svg className="absolute right-3 top-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Stats */}
          <div className="md:col-span-5 lg:col-span-3 flex flex-wrap gap-x-3 gap-y-1 items-center">
            <div className="text-xs text-gray-600">
              <span className="font-medium">Wayleave Stations:</span>
              <span className="ml-1 font-semibold text-blue-600">{filteredWayleaveStations.length}</span>
            </div>
            <div className="text-xs text-gray-600">
              <span className="font-medium">Selected:</span>
              <span className="ml-1 font-semibold text-gray-900">{selectedStation || '-'}</span>
            </div>
          </div>

          {/* Notes filter + Reset Buttons */}
          <div className="md:col-span-1 lg:col-span-1 flex items-end justify-end gap-2">
            <button
              onClick={() => setNotesOnly(!notesOnly)}
              className={`w-8 h-8 flex items-center justify-center border-2 rounded-lg shadow-sm transition-all duration-200 ${
                notesOnly
                  ? 'bg-amber-500 border-amber-500 text-white hover:bg-amber-600'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              title={notesOnly ? 'Showing only stations with notes — click to show all' : 'Show only stations that have notes'}
              aria-pressed={notesOnly}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
            <button
              onClick={handleReset}
              className="w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg shadow-sm transition-all duration-200"
              title="Reset filters"
            >
              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress Status Legend Row */}
        <div className="mt-2 pt-2 border-t border-gray-200">
          <div className="flex flex-wrap gap-x-3 gap-y-1 items-center text-xs">
            <span className="font-medium text-gray-600">Legend:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#fbbf24' }}></div>
              <span className="text-gray-600">H O Docs Received</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f97316' }}></div>
              <span className="text-gray-600">HO Docs Checked</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ea580c' }}></div>
              <span className="text-gray-600">HO Docs Submitted</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#3b82f6' }}></div>
              <span className="text-gray-600">WCG Approval</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#10b981' }}></div>
              <span className="text-gray-600">As-built Submitted</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#9ca3af' }}></div>
              <span className="text-gray-600">No Activity</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map and Wayleave Table Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Map Column - 40% width */}
        <div className="lg:col-span-2 card p-0 overflow-hidden">
          {locations.length === 0 ? (
            <div className="p-12 text-center text-gray-600">
              No wayleave stations found. Stations with wayleave records in the WAYLEAVE table will appear here.
            </div>
          ) : (
            <div
              className={
                isMapExpanded
                  ? 'fixed inset-0 z-[9999] bg-white p-3 sm:p-4 flex flex-col'
                  : 'h-[250px] sm:h-[350px] w-full relative'
              }
            >
            {isMapExpanded && (
              <div className="flex items-center justify-between mb-2 px-1">
                <h3 className="font-semibold text-gray-900 text-base">Wayleave Stations Map</h3>
                <span className="text-xs text-gray-500">Press Esc to close</span>
              </div>
            )}
            <button
              onClick={() => setIsMapExpanded(!isMapExpanded)}
              className="absolute top-2 right-2 z-[1000] w-8 h-8 flex items-center justify-center bg-white hover:bg-gray-50 border-2 border-gray-300 rounded-lg shadow-md transition-all duration-200"
              title={isMapExpanded ? 'Close full-screen map' : 'Pop out map to full screen'}
            >
              {isMapExpanded ? (
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            <div className={isMapExpanded ? 'flex-1 w-full' : 'h-full w-full'}>
            <MapContainer
              ref={mapRef}
              center={getMapCenter()}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
            >
              <MapUpdater center={mapCenter} zoom={mapZoom} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {filteredLocations.map((location, index) => {
                const lat = parseFloat(location.LAT);
                const lng = parseFloat(location.LONG);
                const wayleaveCount = location.WayleaveCount || 0;
                const progressStatus = location.ProgressStatus || 'No Activity';
                const isSelected = selectedStation === location.STATION_NO;

                if (isNaN(lat) || isNaN(lng)) return null;

                return (
                  <Marker
                    key={`${location.STATION_NO}-${isSelected}`}
                    position={[lat, lng]}
                    icon={createWayleaveIcon(progressStatus)}
                    eventHandlers={{
                      click: () => {
                        handleStationClick(location.STATION_NO);
                      },
                    }}
                  >
                    <Popup>
                      <div className="p-3 min-w-[250px]">
                        <p className="font-bold text-lg text-blue-600 mb-2 border-b pb-2">
                          Station: {location.STATION_NO}
                        </p>
                        <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-3">
                          <p className="font-bold text-blue-700">
                            📋 Wayleave Station
                          </p>
                          <p className="text-sm text-blue-600 mt-1">
                            {wayleaveCount} wayleave record{wayleaveCount !== 1 ? 's' : ''}
                          </p>
                          <div className="flex items-center mt-2">
                            <div
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: getStatusColor(progressStatus) }}
                            ></div>
                            <span className="text-xs font-medium text-gray-700">
                              Status: {progressStatus}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1 text-sm">
                          {location.Owner && (
                            <p><span className="font-semibold">Owner:</span> {location.Owner}</p>
                          )}
                          {location.Description && (
                            <p><span className="font-semibold">Description:</span> {location.Description}</p>
                          )}
                          {location.StationType && (
                            <p><span className="font-semibold">Station Type:</span> {location.StationType}</p>
                          )}
                          {location.Route && (
                            <p><span className="font-semibold">Route:</span> {location.Route}</p>
                          )}
                          {location.Road && (
                            <p><span className="font-semibold">Road:</span> {location.Road}</p>
                          )}
                          {location.KM !== null && location.KM !== undefined && (
                            <p><span className="font-semibold">KM:</span> {location.KM}</p>
                          )}
                          {location.Stratum && (
                            <p><span className="font-semibold">Stratum:</span> {location.Stratum}</p>
                          )}
                          {location.Lanes !== null && location.Lanes !== undefined && (
                            <p><span className="font-semibold">Lanes:</span> {location.Lanes}</p>
                          )}
                          <div className="border-t pt-2 mt-2 text-xs text-gray-500">
                            <p>Lat: {lat.toFixed(6)}</p>
                            <p>Long: {lng.toFixed(6)}</p>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* Pulsating overlay for selected station */}
              {selectedStation && (() => {
                // Check all possible sources for the station's coordinates
                let station = locations.find(loc => loc.STATION_NO === selectedStation);
                let lat, lng, progressStatus;

                if (station) {
                  lat = parseFloat(station.LAT);
                  lng = parseFloat(station.LONG);
                  progressStatus = station.ProgressStatus || 'No Activity';
                } else {
                  const tableStation = allWayleaveStations.find(s => s.StationNo === selectedStation);
                  if (!tableStation || !tableStation.LAT || !tableStation.LONG) return null;
                  lat = parseFloat(tableStation.LAT);
                  lng = parseFloat(tableStation.LONG);
                  progressStatus = tableStation.ProgressStatus || 'No Activity';
                }

                if (isNaN(lat) || isNaN(lng)) return null;
                const color = getStatusColor(progressStatus);
                return (
                  <Marker
                    position={[lat, lng]}
                    icon={createPulseIcon(color)}
                    interactive={false}
                  />
                );
              })()}
            </MapContainer>
            </div>
          </div>
        )}
        </div>

        {/* Wayleave Table Column - 60% width */}
        <div ref={tableContainerRef} className="lg:col-span-3 card h-[300px] sm:h-[350px] overflow-y-auto">
          <h3 className="font-semibold text-gray-900 mb-3 text-base">Wayleave Stations</h3>

          {wayleaveTableLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-600">Loading wayleave stations...</div>
            </div>
          ) : filteredWayleaveStations.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {search ? 'No stations match your search.' : 'No wayleave stations found.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  Showing {filteredWayleaveStations.length} of {allWayleaveStations.length} stations
                </span>
                {(stationFilter || descriptionFilter || ownerFilter || statusFilter) && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
              <table className="min-w-[550px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('StationNo')}
                    >
                      <div className="flex items-center">
                        Station No
                        {sortColumn === 'StationNo' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Description')}
                    >
                      <div className="flex items-center">
                        Description
                        {sortColumn === 'Description' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('Owner')}
                    >
                      <div className="flex items-center">
                        Owner
                        {sortColumn === 'Owner' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th
                      className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('ProgressStatus')}
                    >
                      <div className="flex items-center">
                        Progress Status
                        {sortColumn === 'ProgressStatus' && (
                          <span className="ml-1">
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                    </th>
                  </tr>
                  {/* Filter Row */}
                  <tr className="bg-gray-100">
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={stationFilter}
                        onChange={(e) => setStationFilter(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={descriptionFilter}
                        onChange={(e) => setDescriptionFilter(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={ownerFilter}
                        onChange={(e) => setOwnerFilter(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        placeholder="Filter..."
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredWayleaveStations.map((station, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleStationClick(station.StationNo)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {station.StationNo}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {station.Description || '-'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {station.Owner || '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {station.ProgressStatus || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Station Info & Wayleave Details */}
      <div className="card">
        <h3 className="font-semibold text-gray-900 mb-4 text-base">Station Info & Wayleave Details</h3>

        {!selectedStation ? (
          <div className="text-center text-gray-500 py-8">
            <p className="text-sm">Click a station on the map or in the table to view station information and wayleave details</p>
          </div>
        ) : wayleaveLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-600">Loading station information...</div>
          </div>
        ) : wayleaveError ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {wayleaveError}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
            {/* Column 1 - Station Info (4/12) */}
            <div className="sm:col-span-12 lg:col-span-4">
              <div className="card bg-blue-50 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3 pb-2 border-b border-blue-300 text-sm">Station Information</h4>
                <div className="space-y-2 text-xs">
                  <div>
                    <span className="font-medium text-blue-700">Station No:</span>
                    <p className="text-blue-900 font-semibold text-sm">{selectedStation}</p>
                  </div>
                  {stationInfo?.Owner && (
                    <div>
                      <span className="font-medium text-blue-700">Owner:</span>
                      <p className="text-blue-900">{stationInfo.Owner}</p>
                    </div>
                  )}
                  {stationInfo?.Description && (
                    <div>
                      <span className="font-medium text-blue-700">Description:</span>
                      <p className="text-blue-900">{stationInfo.Description}</p>
                    </div>
                  )}
                  {stationInfo?.StationType && (
                    <div>
                      <span className="font-medium text-blue-700">Type:</span>
                      <p className="text-blue-900">{stationInfo.StationType}</p>
                    </div>
                  )}
                  {stationInfo?.Route && (
                    <div>
                      <span className="font-medium text-blue-700">Route:</span>
                      <p className="text-blue-900">{stationInfo.Route}</p>
                    </div>
                  )}
                  {stationInfo?.Road && (
                    <div>
                      <span className="font-medium text-blue-700">Road:</span>
                      <p className="text-blue-900">{stationInfo.Road}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Column 2 - Wayleave Records (8/12) */}
            <div className="sm:col-span-12 lg:col-span-8">
              {wayleaveData.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 text-gray-700 px-3 py-2 rounded-lg">
                  <p className="font-semibold text-xs">No Wayleave Records</p>
                  <p className="text-xs mt-1">No wayleave records found for this station.</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {wayleaveData.map((record, index) => {
                    // Define milestones in order
                    const milestones = [
                      { key: 'H O Docs Recevied', label: 'H O Docs Received' },
                      { key: 'HO Docs Checked', label: 'HO Docs Checked' },
                      { key: 'HO Docs Submitted', label: 'HO Docs Submitted' },
                      { key: 'WCG Approval Obtained', label: 'WCG Approval Obtained' },
                      { key: 'As-built Submitted', label: 'As-built Submitted' }
                    ];

                    // Format date to Month-Year
                    const formatDate = (dateStr) => {
                      if (!dateStr) return null;
                      const date = new Date(dateStr);
                      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      return `${months[date.getMonth()]} ${date.getFullYear()}`;
                    };

                    return (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                        {/* Milestone Timeline */}
                        <div className="mb-4">
                          <p className="font-semibold text-sm text-gray-700 mb-3">Milestone Progress</p>
                          <div className="relative overflow-x-auto pb-2">
                            {/* Progress Line */}
                            <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200" style={{ zIndex: 0 }}></div>

                            {/* Milestones */}
                            <div className="relative flex justify-between min-w-[480px]" style={{ zIndex: 1 }}>
                              {milestones.map((milestone, idx) => {
                                const date = record[milestone.key];
                                const isCompleted = !!date;
                                const formattedDate = formatDate(date);

                                return (
                                  <div key={idx} className="flex flex-col items-center" style={{ width: '20%' }}>
                                    {/* Circle */}
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                                      isCompleted
                                        ? 'bg-green-500 border-green-600 text-white'
                                        : 'bg-gray-200 border-gray-300 text-gray-500'
                                    }`}>
                                      {idx + 1}
                                    </div>

                                    {/* Label */}
                                    <div className="text-center mt-2 px-1">
                                      <p className={`text-xs font-medium ${isCompleted ? 'text-green-700' : 'text-gray-500'}`}>
                                        {milestone.label}
                                      </p>
                                      {formattedDate && (
                                        <p className="text-xs text-green-600 font-semibold mt-1">
                                          {formattedDate}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* Record Details */}
                        <div className="border-t border-gray-200 pt-3">
                          <p className="font-semibold text-sm text-gray-700 mb-2">Record Details</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                            {Object.entries(record).map(([key, value]) => {
                              // Exclude redundant and system columns, and milestone columns (shown in timeline)
                              if (key === 'RowNum' || key === 'ID' || key.toLowerCase() === 'id' ||
                                  key === 'No.' || (key.toLowerCase().includes('station') && key !== 'Status') ||
                                  milestones.some(m => m.key === key)) return null;
                              return (
                                <div key={key} className="text-xs">
                                  <span className="font-medium text-gray-600">{key}: </span>
                                  <span className="text-gray-900">
                                    {value !== null && value !== undefined ? String(value) : '-'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Notes (separate block, always expanded when a station is selected) */}
      {selectedStation && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-semibold text-gray-900 text-base">Notes</h3>
            {notes.length > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                {notes.length}
              </span>
            )}
            <span className="text-xs text-gray-400 ml-1">Station {selectedStation}</span>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            {/* Existing notes */}
            <div className="mb-3 max-h-64 overflow-y-auto">
              {notesLoading ? (
                <p className="text-xs text-gray-500">Loading notes...</p>
              ) : notes.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No notes yet for this station.</p>
              ) : (
                <div className="space-y-2">
                  {notes.map((note) => (
                    <div key={note.id} className="bg-white border border-amber-200 rounded p-2">
                      {editingNoteId === note.id ? (
                        <div>
                          <textarea
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            rows={2}
                            className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                            autoFocus
                          />
                          <div className="flex gap-1.5 mt-1.5">
                            <button
                              onClick={() => updateNote(note.id)}
                              disabled={!editingNoteText.trim()}
                              className="px-2.5 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40 transition-colors"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                              className="px-2.5 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{renderNoteText(note.note_text)}</p>
                          <div className="flex items-center justify-between mt-1.5">
                            <div className="flex items-center gap-2 text-[10px] text-gray-400">
                              <span className="font-medium">{note.created_by}</span>
                              <span>—</span>
                              <span>{new Date(note.created_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.note_text); }}
                                className="p-1 text-gray-400 hover:text-amber-600 rounded transition-colors"
                                title="Edit note"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => { if (window.confirm('Delete this note?')) deleteNote(note.id); }}
                                className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                                title="Delete note"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add note input */}
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={noteInputRef}
                  value={noteText}
                  onChange={handleNoteChange}
                  onKeyDown={handleNoteKeyDown}
                  placeholder="Add a note... (type @ to mention someone)"
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                />
                {showMentionDropdown && getFilteredMentionUsers().length > 0 && (
                  <div className="absolute bottom-full left-0 mb-1 w-64 max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {getFilteredMentionUsers().map((user, idx) => (
                      <div
                        key={user.username}
                        className={`px-3 py-2 cursor-pointer text-sm flex items-center gap-2 ${idx === mentionIndex ? 'bg-amber-50 text-amber-900' : 'hover:bg-gray-50 text-gray-700'}`}
                        onMouseDown={(e) => { e.preventDefault(); insertMention(user); }}
                        onMouseEnter={() => setMentionIndex(idx)}
                      >
                        <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {(user.firstName?.[0] || user.username[0]).toUpperCase()}
                        </span>
                        <span className="font-medium">@{user.username}</span>
                        {user.firstName && <span className="text-gray-400 text-xs">{user.firstName} {user.lastName || ''}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={saveNote}
                disabled={!noteText.trim() || noteSaving}
                className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors self-end"
              >
                {noteSaving ? 'Saving...' : 'Add Note'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

export default Wayleave;
